import type { ApolloCache } from "@apollo/client";
import { isReference } from "@apollo/client/utilities";

type CacheWithReadField = ApolloCache & {
  readField<T = unknown>(
    fieldName: string,
    from: unknown,
    args?: Record<string, unknown>
  ): T | undefined;
};

const readPlainField = <T = unknown>(
  from: unknown,
  fieldName: string
): T | undefined => {
  if (!from || typeof from !== "object" || isReference(from)) {
    return undefined;
  }
  return (from as Record<string, T | undefined>)[fieldName];
};

/** Apollo's cache typing omits readField on ApolloCache in some TS versions. */
export const readCacheField = <T = unknown>(
  cache: ApolloCache,
  fieldName: string,
  from: unknown,
  args?: Record<string, unknown>
): T | undefined => {
  if (from && typeof from === "object" && !isReference(from)) {
    const plain = readPlainField<T>(from, fieldName);
    if (plain !== undefined) {
      return plain;
    }
  }

  try {
    const fromCache = (cache as CacheWithReadField).readField<T>(
      fieldName,
      from,
      args
    );
    if (fromCache !== undefined) {
      return fromCache;
    }
  } catch {
    // Fall through to plain-object read.
  }

  return readPlainField<T>(from, fieldName);
};
